"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { useMotionMode } from "@/context/MotionContext";

// =============================================================================
// LETTER GLITCH HOOK
// =============================================================================
// Pure JS text glitching - zero GPU cost
// Similar-glyph character swaps for degraded space transmission aesthetic
//
// Rules:
// - Max 2 letters glitching simultaneously
// - Duration: 80-120ms per event
// - Frequency: every 4-8s idle, + on trigger events
// - Only fires on stable text (not mid-animation)

// Character swap pool - similar glyphs only
export const GLITCH_MAP: Record<string, string> = {
  V: "∀",
  N: "И",
  G: "6",
  U: "∩",
  I: "|",
  E: "3",
  A: "4",
  O: "0",
  // Lowercase versions
  v: "∀",
  n: "и",
  g: "6",
  u: "∩",
  i: "|",
  e: "3",
  a: "4",
  o: "0",
};

const GLITCH_CHARS = Object.keys(GLITCH_MAP);
const MIN_DURATION = 80;
const MAX_DURATION = 120;
const MIN_IDLE_INTERVAL = 2000;
const MAX_IDLE_INTERVAL = 3500;
const MAX_SIMULTANEOUS_GLITCHES = 2;

interface UseLetterGlitchOptions {
  /** Enable idle glitching (every 2-3.5s) */
  idleGlitch?: boolean;
  /** GPU tier - T0/T1 disable glitch, T2 event-only, T3 full */
  tier?: number;
  /** Multiplier for idle interval (0.5 = 2x faster, 2 = 2x slower). Default 1. */
  intervalMultiplier?: number;
  /** Callback fired when a glitch triggers (for audio) */
  onGlitch?: () => void;
  /** External enable/disable control (e.g., CRT toggle). Default true. */
  enabled?: boolean;
}

interface UseLetterGlitchReturn {
  /** The current text (may have glitched characters) */
  text: string;
  /** Trigger a glitch event manually (e.g., on hover/select) */
  trigger: () => void;
  /** Whether a glitch is currently active */
  isGlitching: boolean;
}

export function useLetterGlitch(
  originalText: string,
  options: UseLetterGlitchOptions = {}
): UseLetterGlitchReturn {
  const { idleGlitch = true, tier, intervalMultiplier = 1, onGlitch, enabled = true } = options;
  const { gpuTier } = useMotionMode();
  const effectiveTier = tier ?? gpuTier;

  const [displayText, setDisplayText] = useState(originalText);
  const [isGlitching, setIsGlitching] = useState(false);
  const isGlitchingRef = useRef(false); // Ref to avoid callback recreation
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const idleTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Glitch is disabled for T0/T1 or when externally disabled (e.g., CRT off)
  const isEnabled = enabled && effectiveTier >= 2;
  // Idle glitch only for T3
  const hasIdleGlitch = idleGlitch && enabled && effectiveTier >= 3;

  // Find glitchable character indices
  const getGlitchableIndices = useCallback((text: string): number[] => {
    const indices: number[] = [];
    for (let i = 0; i < text.length; i++) {
      if (GLITCH_CHARS.includes(text[i])) {
        indices.push(i);
      }
    }
    return indices;
  }, []);

  // Apply glitch to text
  const applyGlitch = useCallback(
    (text: string, indices: number[]): string => {
      const chars = text.split("");
      indices.forEach((idx) => {
        const original = chars[idx];
        if (GLITCH_MAP[original]) {
          chars[idx] = GLITCH_MAP[original];
        }
      });
      return chars.join("");
    },
    []
  );

  // Trigger a glitch event
  const trigger = useCallback(() => {
    if (!isEnabled || isGlitchingRef.current) return;

    const glitchableIndices = getGlitchableIndices(originalText);
    if (glitchableIndices.length === 0) return;

    // Pick 1-2 random indices to glitch
    const numGlitches = Math.min(
      MAX_SIMULTANEOUS_GLITCHES,
      Math.floor(Math.random() * 2) + 1,
      glitchableIndices.length
    );

    const shuffled = [...glitchableIndices].sort(() => Math.random() - 0.5);
    const selectedIndices = shuffled.slice(0, numGlitches);

    // Apply glitch
    isGlitchingRef.current = true;
    setIsGlitching(true);
    setDisplayText(applyGlitch(originalText, selectedIndices));
    onGlitch?.();

    // Clear after random duration
    const duration =
      MIN_DURATION + Math.random() * (MAX_DURATION - MIN_DURATION);
    timeoutRef.current = setTimeout(() => {
      setDisplayText(originalText);
      isGlitchingRef.current = false;
      setIsGlitching(false);
    }, duration);
  }, [isEnabled, originalText, getGlitchableIndices, applyGlitch, onGlitch]);

  // Schedule next idle glitch
  const scheduleIdleGlitch = useCallback(() => {
    if (!hasIdleGlitch) return;

    const baseDelay =
      MIN_IDLE_INTERVAL +
      Math.random() * (MAX_IDLE_INTERVAL - MIN_IDLE_INTERVAL);
    const delay = baseDelay * intervalMultiplier;

    idleTimeoutRef.current = setTimeout(() => {
      trigger();
      scheduleIdleGlitch();
    }, delay);
  }, [hasIdleGlitch, trigger, intervalMultiplier]);

  // Start/stop idle glitching
  useEffect(() => {
    if (hasIdleGlitch) {
      scheduleIdleGlitch();
    }

    return () => {
      if (idleTimeoutRef.current) {
        clearTimeout(idleTimeoutRef.current);
      }
    };
  }, [hasIdleGlitch, scheduleIdleGlitch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Update display text when original changes
  useEffect(() => {
    if (!isGlitching) {
      setDisplayText(originalText);
    }
  }, [originalText, isGlitching]);

  return {
    text: displayText,
    trigger,
    isGlitching,
  };
}
